// Every version of a Chapter's script, with where it came from and a way back
// to it.
//
// DESIGN.md promises that "an Instruction never costs the Author their edits":
// a re-run makes a new version and the old one, edits included, stays
// restorable. That promise is only real if the Author can see the versions, so
// each row says what made it (a Refinement, an Instruction, their own edit),
// when, and how big it is.

import type { ScriptVersionId, ScriptVersionSummary } from "@contract";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { cx } from "../../../components/cx";
import { DateTime } from "../../../components/format/DateTime";
import { Num } from "../../../components/format/Num";
import { Dialog } from "../../../components/overlay/Dialog";

/** The source label, spelled out rather than reached by a template key, so check-i18n can see all three. */
function SourceLabel({ version }: { version: ScriptVersionSummary }) {
  const { t } = useTranslation("review");
  if (version.source === "edit") return <>{t("version.source.edit")}</>;
  if (version.source === "restore") return <>{t("version.source.restore")}</>;
  return <>{t("version.source.refinement")}</>;
}

export function VersionsDialog({
  open,
  versions,
  viewingId,
  restorable,
  busy,
  onView,
  onRestore,
  onClose,
}: {
  open: boolean;
  /** Newest first, as the API returns them. */
  versions: readonly ScriptVersionSummary[];
  viewingId: ScriptVersionId | undefined;
  /** False while the Chapter is locked by a render: nothing may change its script. */
  restorable: boolean;
  busy: boolean;
  onView: (version: ScriptVersionSummary) => void;
  onRestore: (version: ScriptVersionSummary) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("review");
  return (
    <Dialog open={open} onClose={onClose} title={t("version.title")} width="lg">
      {/* The history scrolls inside the dialog. A Chapter re-run a dozen times
          with German Instructions ran the panel past three thousand pixels in
          an eight-hundred-pixel viewport, and neither the first version nor the
          last could be reached with the wheel. */}
      <ol className="m-0 flex max-h-[60vh] list-none flex-col overflow-y-auto p-0">
        {versions.map((version) => {
          const viewing =
            viewingId === version.id || (viewingId === undefined && version.isCurrent);
          return (
            <li
              key={version.id}
              data-version={version.number}
              className={cx(
                "flex items-baseline gap-3 border-line-soft border-b py-2.5 last:border-b-0",
                viewing && "bg-accent-soft",
              )}
            >
              <span className="w-8 shrink-0 font-mono text-data text-ink">{version.number}</span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="m-0 flex flex-wrap items-center gap-2 font-mono text-label text-ink-2">
                  <SourceLabel version={version} />
                  <span aria-hidden="true">·</span>
                  <DateTime value={version.createdAt} style="dateTime" />
                  <span aria-hidden="true">·</span>
                  <span>
                    {t("version.blocks", { count: version.blockCount })}
                    {", "}
                    <Num value={version.characterCount} />
                  </span>
                  {version.editedBlocks > 0 ? (
                    <span className="text-accent">
                      {t("version.editedBlocks", { count: version.editedBlocks })}
                    </span>
                  ) : null}
                  {version.isCurrent ? (
                    <span className="text-accent">{t("version.current")}</span>
                  ) : null}
                  {version.isApproved ? (
                    <span className="text-ok">{t("version.approved")}</span>
                  ) : null}
                </p>
                {version.instruction ? (
                  <p className="m-0 font-serif text-book-sm text-ink">
                    {t("version.instruction", { text: version.instruction.text })}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => onView(version)}
                  disabled={viewing}
                >
                  {t("version.view")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onRestore(version)}
                  disabled={!restorable || busy || version.isCurrent}
                >
                  {t("version.restore")}
                </Button>
              </div>
            </li>
          );
        })}
      </ol>
    </Dialog>
  );
}
